import type { GrammarError } from '$lib/types/grammar'

/**
 * Find text position in a string with fallback search
 */
export function findTextPosition(
  text: string,
  searchText: string,
  expectedStart: number,
  searchRadius = 50
): { start: number; end: number } | null {
  // Normalize line endings for consistent matching
  const normalizedText = text.replace(/\r\n/g, '\n')
  const normalizedSearch = searchText.replace(/\r\n/g, '\n')
  
  // First try exact position
  const exactMatch = normalizedText.substring(expectedStart, expectedStart + normalizedSearch.length)
  if (exactMatch === normalizedSearch) {
    return {
      start: expectedStart,
      end: expectedStart + normalizedSearch.length
    }
  }
  
  // Try to find it near the expected position
  const searchStart = Math.max(0, expectedStart - searchRadius)
  const searchEnd = Math.min(normalizedText.length, expectedStart + normalizedSearch.length + searchRadius)
  const searchArea = normalizedText.substring(searchStart, searchEnd)
  
  const indexInSearchArea = searchArea.indexOf(normalizedSearch)
  
  if (indexInSearchArea !== -1) {
    const actualStart = searchStart + indexInSearchArea
    return {
      start: actualStart,
      end: actualStart + normalizedSearch.length
    }
  }
  
  // Fallback: find first occurrence in entire text
  const index = normalizedText.indexOf(normalizedSearch)
  if (index !== -1) {
    return {
      start: index,
      end: index + normalizedSearch.length
    }
  }
  
  return null
}

/**
 * Apply a single correction to text
 */
export function applyCorrection(
  text: string,
  originalText: string,
  replacementText: string,
  expectedStart: number
): { newText: string; offset: number } | null {
  const position = findTextPosition(text, originalText, expectedStart)
  
  if (!position) {
    return null
  }
  
  const newText = 
    text.substring(0, position.start) + 
    replacementText + 
    text.substring(position.end)
  
  const offset = replacementText.length - originalText.length
  
  return { newText, offset }
}

/**
 * Update error positions after text correction
 */
export function updateErrorPositions(
  errors: GrammarError[],
  correctionStart: number,
  offset: number
): GrammarError[] {
  return errors.map(error => {
    // Handle both old format (startPos/endPos) and new format (position.start/end)
    const errorStart = (error as any).startPos ?? error.position?.start
    const errorEnd = (error as any).endPos ?? error.position?.end
    
    if (errorStart > correctionStart) {
      // Update based on the format the error uses
      if ((error as any).startPos !== undefined) {
        // Old format - preserve all fields including aiComment
        return {
          ...error,
          startPos: (error as any).startPos + offset,
          endPos: (error as any).endPos + offset,
          aiComment: error.aiComment // Preserve AI comment
        } as any
      } else if (error.position) {
        // New format - preserve all fields including aiComment
        return {
          ...error,
          position: {
            ...error.position,
            start: error.position.start + offset,
            end: error.position.end + offset
          },
          aiComment: error.aiComment // Preserve AI comment
        }
      }
    }
    // Return error unchanged if position doesn't need adjustment
    return {
      ...error,
      aiComment: error.aiComment // Preserve AI comment even if position unchanged
    }
  })
}

/**
 * Find and update error positions in text
 */
export function updateErrorsForText(
  errors: GrammarError[],
  currentText: string
): GrammarError[] {
  return errors
    .map(error => {
      const originalText = error.originalText || error.original
      if (!originalText) return null
      
      // Get the expected position from either format
      const expectedStart = (error as any).startPos ?? error.position?.start ?? 0
      
      // Use findTextPosition for consistent position finding
      const position = findTextPosition(
        currentText,
        originalText,
        expectedStart,
        100 // Larger radius for error search
      )
      
      if (!position) return null
      
      // Update based on the format the error uses
      if ((error as any).startPos !== undefined) {
        // Old format
        return {
          ...error,
          startPos: position.start,
          endPos: position.end
        }
      } else if (error.position) {
        // New format
        return {
          ...error,
          position: {
            start: position.start,
            end: position.end
          }
        }
      } else {
        // Default to new format
        return {
          ...error,
          position: {
            start: position.start,
            end: position.end
          }
        }
      }
    })
    .filter((error): error is GrammarError => error !== null)
}

/**
 * Apply multiple corrections in reverse order (from end to start)
 */
export function applyAllCorrections(
  text: string,
  errors: GrammarError[]
): { newText: string; appliedCount: number } {
  // Sort errors from end to start to avoid position shifts
  const sortedErrors = [...errors]
    .filter(error => error.corrections.length > 0 && error.originalText)
    .sort((a, b) => {
      const aPos = (a as any).startPos ?? a.position?.start ?? 0
      const bPos = (b as any).startPos ?? b.position?.start ?? 0
      return bPos - aPos
    })
  
  let newText = text
  let appliedCount = 0
  let totalOffset = 0
  
  for (const error of sortedErrors) {
    // Check if AI suggests keeping original
    if (error.aiComment && !error.aiComment.agreesWithCorrection) {
      // AI disagrees - skip this correction (keep original)
      continue
    }
    
    const correction = error.corrections[0]
    if (!correction || !error.originalText) continue
    
    // Handle both old format (startPos) and new format (position.start)
    const startPos = (error as any).startPos ?? error.position?.start ?? 0
    
    const result = applyCorrection(
      newText,
      error.originalText,
      correction.text,
      startPos + totalOffset
    )
    
    if (result) {
      newText = result.newText
      totalOffset += result.offset
      appliedCount++
    }
  }
  
  return { newText, appliedCount }
}